import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { ModuleInfoDto } from '../../types/subscriptions';
import { getCategoryLabel, getCategoryColor } from '../../utils/subscriptions';
import { useState } from 'react';

interface DependencyResolutionDialogProps {
  open: boolean;
  onClose: () => void;
  module: ModuleInfoDto;
  missingDependencies: ModuleInfoDto[];
  willAutoEnable: ModuleInfoDto[];
  onConfirm: (autoResolve: boolean) => void;
  isLoading?: boolean;
}

/**
 * Dialog shown when a user tries to enable a module with missing dependencies.
 * Allows user to choose between auto-enabling dependencies or manual enablement.
 */
export const DependencyResolutionDialog = ({
  open,
  onClose,
  module,
  missingDependencies,
  willAutoEnable,
  onConfirm,
  isLoading = false,
}: DependencyResolutionDialogProps) => {
  const [autoResolve, setAutoResolve] = useState(true);

  const handleConfirm = () => {
    onConfirm(autoResolve);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setAutoResolve(true); // Reset to default
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">Missing Dependencies</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Module being enabled */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              You're enabling:
            </Typography>
            <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={600}>
                    {module.name}
                  </Typography>
                  <Chip
                    label={getCategoryLabel(module.category)}
                    size="small"
                    sx={{
                      bgcolor: getCategoryColor(module.category),
                      color: 'white',
                      fontWeight: 500,
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {module.description}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Warning about missing dependencies */}
          <Alert severity="warning" icon={<WarningAmberIcon />}>
            This module requires <strong>{missingDependencies.length}</strong>{' '}
            {missingDependencies.length === 1 ? 'dependency' : 'dependencies'} that{' '}
            {missingDependencies.length === 1 ? 'is' : 'are'} not currently enabled.
          </Alert>

          {/* List of missing dependencies */}
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Required Dependencies:
            </Typography>
            <Stack spacing={1.5} sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {missingDependencies.map((dep) => (
                <Card key={dep.id} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" fontWeight={600}>
                        {dep.name}
                      </Typography>
                      <Chip
                        label={getCategoryLabel(dep.category)}
                        size="small"
                        sx={{
                          bgcolor: getCategoryColor(dep.category),
                          color: 'white',
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                      {dep.isCore && (
                        <Chip label="Core" size="small" color="primary" sx={{ height: 20 }} />
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {dep.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>

          <Divider />

          {/* Auto-resolve option */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoResolve}
                  onChange={(e) => setAutoResolve(e.target.checked)}
                  disabled={isLoading}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  Automatically enable {willAutoEnable.length}{' '}
                  {willAutoEnable.length === 1 ? 'dependency' : 'dependencies'}
                </Typography>
              }
            />

            {autoResolve ? (
              <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mt: 1 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    The following modules will be enabled automatically:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {willAutoEnable.map((mod) => (
                      <Chip
                        key={mod.id}
                        label={mod.name}
                        size="small"
                        variant="outlined"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                    <ArrowForwardIcon sx={{ fontSize: 18, alignSelf: 'center', mx: 0.5 }} />
                    <Chip
                      label={module.name}
                      size="small"
                      color="primary"
                      sx={{ mb: 0.5 }}
                    />
                  </Stack>
                </Stack>
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  You will need to manually enable the required dependencies in the correct
                  order before enabling <strong>{module.name}</strong>.
                </Typography>
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
          startIcon={autoResolve ? <CheckCircleIcon /> : undefined}
        >
          {autoResolve
            ? `Enable ${willAutoEnable.length + 1} Modules`
            : 'Continue Anyway'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
